import { api, publicApi } from 'src/api/axios';

import { httpStatus, isHttpStatus } from '../usePublicTokenResource';

/**
 * Forked W2 enrollment API (ADR-0011). These endpoints live behind the
 * authenticated `/employee/*` surface; the untouched B2C `/auth/*` path mints the
 * account + JWT first (see `authService`). All W2 linkage is token-scoped: the
 * employer token (in the URL) selects which `employees` row / PHQ applies.
 *
 * Backend (parallel work on `fm/w2phqbe-h7`): `POST /employee/enroll`,
 * `POST /employee/phq`, `GET /public/link/:token` on http://localhost:3000/api.
 */

export type W2LinkStatus = 'Active' | 'Disabled' | 'Archived';

export type PhqStatus = 'NotStarted' | 'InProgress' | 'Completed' | 'Waived';

export interface W2PublicLink {
  /** Employer link gate: Active renders the flow; Disabled/Archived → error. */
  status: W2LinkStatus;
  employerName?: string;
  /** Branding blob resolved for the employer/group (logo, colors, ...). */
  branding?: Record<string, unknown>;
}

export interface W2EnrollResult {
  phqStatus: PhqStatus;
  /**
   * Post-PHQ handoff (phqland-s4 §3.2): true when the employee's group is
   * Active + Self + Small_2_9 with offerings/contribution ready, so the fresh
   * submit can hop straight to `/select-plans`. OPTIONAL — the backend half
   * ships in parallel (virgil `fm/selphq-b2`); absent/non-`true` must mean
   * exactly today's behavior (the done terminal), so consumers gate on
   * `=== true`, never truthiness of a decoded shape.
   */
  selectionAvailable?: boolean;
}

/**
 * The roster invite pre-fill: the three fields the public per-employee endpoint
 * returns for a still-claimable roster row. Never more than these (the endpoint
 * caps it server-side; a claimed/mismatched/unknown ref 404s).
 */
export interface W2InvitePrefill {
  firstName: string;
  lastName: string;
  email: string;
}

/**
 * The FULL B2C questionnaire payload persisted to `employees.phqPayload` (jsonb).
 * The reworked W2 flow reuses the entire B2C profile + PHQ, so this is the whole
 * onboarding `data` object (applicants + householdIncome + healthQuestions, …) —
 * an open shape, matching the backend DTO (`phqPayload?: Record<string, any>`,
 * "parity with applications.payload"), which stores it verbatim with no
 * shape validation (ADR-0003/0011).
 */
export type W2PhqPayload = Record<string, unknown> & { version?: string };

/** A terminal PHQ can never be re-filled (one-shot immutable, per ADR-0011). */
export function isTerminalPhq(status: PhqStatus): boolean {
  return status === 'Completed' || status === 'Waived';
}

/**
 * The POSITIVE side of the same gate: only an explicitly decoded, genuinely
 * mid-flow status may open the editable PHQ. `enroll` returns the wire body
 * unvalidated, so a malformed 2xx (missing/garbage `phqStatus`) is "unknown",
 * NOT "not terminal" — an unknown status must fail closed, never open a PHQ the
 * employee may already have completed or waived (fe-cx #5 round 2).
 */
export function isEditablePhq(status: unknown): status is PhqStatus {
  return status === 'NotStarted' || status === 'InProgress';
}

interface RawPublicLink {
  status?: string;
  tokenStatus?: string;
  employerName?: string;
  name?: string;
  branding?: Record<string, unknown>;
}

function normalizeStatus(raw: string | undefined): W2LinkStatus | null {
  switch (raw) {
    case 'Active':
    case 'Disabled':
    case 'Archived':
      return raw;
    default:
      return null;
  }
}

/**
 * Resolve branding + link status for the employer token BEFORE any auth. Returns
 * `null` when the token is unknown / invalid (so the caller shows the branded
 * error page), and throws only on unexpected transport errors.
 */
export async function getPublicLink(
  token: string
): Promise<W2PublicLink | null> {
  try {
    // Public, pre-auth endpoint: must NOT carry the JWT (it 403s if present).
    const res = await publicApi.get<RawPublicLink>(
      `/public/link/${encodeURIComponent(token)}`
    );
    const data = res.data ?? {};
    const status = normalizeStatus(data.status ?? data.tokenStatus);
    if (!status) return null;
    return {
      status,
      employerName: data.employerName ?? data.name,
      branding: data.branding,
    };
  } catch (err) {
    // 404 / 400 → invalid token → branded error (not a crash).
    if (isHttpStatus(err, [400, 404])) return null;
    // Unexpected outage → the caller shows a retry. Log status-only for
    // observability (never the payload/token) — finding M2.
    console.error('[w2] public link resolve failed', {
      status: httpStatus(err),
    });
    throw err;
  }
}

/**
 * Exchange an OPAQUE per-employee invite `ref` (the `?emp=` on a roster-invite
 * link) for that roster row's `{ firstName, lastName, email }`, to pre-fill the
 * register form. Same public, pre-auth surface as {@link getPublicLink}.
 *
 * The pre-fill is an ENHANCEMENT, never a blocker: a bad/claimed/mismatched ref
 * (404 `error.employeeNotFound`) OR any transport failure resolves to `null`, so
 * the caller renders the register form exactly as today (empty, all editable) —
 * this function never throws. A missing field is treated as no-prefill for the
 * same reason. Status-only logging on unexpected errors (never the token/PII —
 * finding M2).
 */
export async function getInviteEmployee(
  token: string,
  ref: string
): Promise<W2InvitePrefill | null> {
  try {
    // Public, pre-auth endpoint: must NOT carry the JWT (mirrors getPublicLink).
    const res = await publicApi.get<Partial<W2InvitePrefill>>(
      `/public/link/${encodeURIComponent(token)}/employee/${encodeURIComponent(
        ref
      )}`
    );
    const { firstName, lastName, email } = res.data ?? {};
    if (!firstName || !lastName || !email) return null;
    return { firstName, lastName, email };
  } catch (err) {
    // 404 (unknown/mismatched/claimed ref) is an EXPECTED, quiet miss. Anything
    // else is also swallowed (the ref never blocks the form) but logged
    // status-only for observability.
    if (!isHttpStatus(err, [400, 404])) {
      console.error('[w2] invite prefill resolve failed', {
        status: httpStatus(err),
      });
    }
    return null;
  }
}

/**
 * Create-or-match the `employees` row for `(employer-from-token, current user)`
 * and return the current `phqStatus` so the client can gate on immutability
 * before rendering the PHQ. Requires the JWT (attached by the axios interceptor).
 */
export async function enroll(token: string): Promise<W2EnrollResult> {
  const res = await api.post<W2EnrollResult>('/employee/enroll', { token });
  return res.data;
}

/** Complete the PHQ (writes payload, flips → Completed). Token-scoped. */
export async function submitPhq(
  token: string,
  phqPayload: W2PhqPayload
): Promise<W2EnrollResult> {
  const res = await api.post<W2EnrollResult>('/employee/phq', {
    token,
    phqPayload,
  });
  return res.data;
}

/**
 * Waive the PHQ (no health answers, flips → Waived). Token-scoped.
 *
 * Called by `W2WaiverModal` (spec screen 2) when the employee selects "I waive
 * coverage" + certifies. `certificationText` carries the verbatim waiver legalese
 * the employee accepted; it is OPTIONAL on the backend (a parallel crew is adding
 * the column) — the endpoint works today without it, so we always send it but
 * never block on it.
 */
export async function waivePhq(
  token: string,
  certificationText?: string
): Promise<W2EnrollResult> {
  const res = await api.post<W2EnrollResult>('/employee/phq', {
    token,
    waived: true,
    ...(certificationText ? { certificationText } : {}),
  });
  return res.data;
}
